import { STORAGE_KEYS } from '../constants/storage.constants';
import { AnalyticsUtil } from './analytics.util';
import { logger } from './logger.util';

type RouteHandler = () => void;

class Router {
  private routes: Map<string, RouteHandler> = new Map();
  private currentRoute: string = '';

  constructor() {
    window.addEventListener('hashchange', () => this.handleRoute());
    // Usar DOMContentLoaded ao invés de load para garantir que execute mais cedo
    // Mas ainda aguardar um tick para garantir que callbacks estejam configurados
    window.addEventListener('load', () => {
      // Pequeno delay para garantir que callbacks estejam configurados
      setTimeout(() => this.handleRoute(), 0);
    });
  }

  register(path: string, handler: RouteHandler): void {
    this.routes.set(path, handler);
  }

  navigate(path: string): void {
    logger.debug('[Router] navigate() chamado:', path);
    window.location.hash = path;
  }

  getCurrentRoute(): string {
    return this.currentRoute;
  }

  private handleRoute(): void {
    const hash = window.location.hash.slice(1) || '/';

    // Extrair query params do hash antes de processar a rota
    // Exemplo: /home?cid=123 -> route: /home, query: cid=123
    let route = hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex >= 0) {
      const queryString = hash.substring(queryIndex + 1);
      route = hash.substring(0, queryIndex);

      // Salvar query params no sessionStorage para uso posterior
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        // Usar chave específica para 'cid', outras chaves genéricas
        const storageKey = key === 'cid'
          ? STORAGE_KEYS.QUERY_PARAM_CID
          : `query_param_${key}`;
        sessionStorage.setItem(storageKey, value);
        logger.debug('[Router] Query param salvo:', key);
      });
    }

    route = route.startsWith('/') ? route : `/${route}`;

    // Não processar se já está na mesma rota (evita chamadas duplicadas)
    if (this.currentRoute === route) {
      logger.debug('[Router] Já está na rota, ignorando chamada duplicada');
      return;
    }

    this.currentRoute = route;

    logger.debug('[Router] handleRoute() - Rota processada:', route);

    // Tentar match exato primeiro
    let handler = this.routes.get(route);

    // Se não encontrou, tentar match com parâmetros
    if (!handler) {
      logger.debug('[Router] Rota exata não encontrada, tentando match com parâmetros...');
      for (const [registeredPath, routeHandler] of this.routes.entries()) {
        if (this.matchRoute(registeredPath, route)) {
          handler = routeHandler;
          logger.debug('[Router] Match encontrado com padrão:', registeredPath);
          break;
        }
      }
    }

    if (handler) {
      logger.debug('[Router] Executando handler da rota:', route);

      // Rastrear visualização de página no GA4
      AnalyticsUtil.trackPageView(route);

      handler();
    } else {
      logger.debug('[Router] Handler não encontrado, usando rota padrão (/)');
      // Rota padrão
      const defaultHandler = this.routes.get('/');
      if (defaultHandler) {
        defaultHandler();
      } else {
        logger.warnWithContext('Router', 'Rota padrão (/) também não encontrada!');
      }
    }
  }

  private matchRoute(pattern: string, path: string): boolean {
    // Converter padrão com :param para regex
    const patternRegex = pattern.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${patternRegex}$`);
    return regex.test(path);
  }
}

export const router = new Router();

